import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Box, Sparkles, RefreshCw, ChefHat } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { LessonWrapper, LessonPage } from '../../ui/LessonWrapper';

// ============ LESSON CONTENT ============
const LESSONS: LessonPage[] = [
    {
        id: 1,
        title: "What is a Function?",
        icon: "👩‍🍳",
        content: (
            <div className="space-y-6">
                <div className="bg-purple-50 p-6 rounded-2xl border-2 border-purple-200">
                    <h3 className="font-bold text-xl text-purple-800 mb-3">The Code Machine</h3>
                    <p className="text-slate-700 mb-4">
                        A <strong>Function</strong> is like a machine or a recipe. You give it ingredients,
                        it does some work, and it gives you back a result.
                    </p>
                    <p className="text-slate-700">
                        Instead of writing the same steps over and over, you wrap them in a function
                        and just call the function name when you need it!
                    </p>
                </div>
            </div>
        )
    },
    {
        id: 2,
        title: "Inputs & Outputs",
        icon: "🔄",
        content: (
            <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-blue-50 p-6 rounded-xl border-2 border-blue-200">
                        <h3 className="font-bold text-blue-800 mb-2">📥 Input (Arguments)</h3>
                        <p className="text-slate-600 text-sm">
                            The "ingredients" you put INTO the machine.
                            <br /><br />
                            Example: <code className="bg-blue-100 px-1 rounded">bake(<strong>"Flour", "Eggs"</strong>)</code>
                        </p>
                    </div>
                    <div className="bg-green-50 p-6 rounded-xl border-2 border-green-200">
                        <h3 className="font-bold text-green-800 mb-2">📤 Output (Return)</h3>
                        <p className="text-slate-600 text-sm">
                            The "product" the machine gives BACK.
                            <br /><br />
                            Example: Returns a <strong className="text-green-600">Cake 🎂</strong>
                        </p>
                    </div>
                </div>
            </div>
        )
    },
    {
        id: 3,
        title: "Scope",
        icon: "🔭",
        content: (
            <div className="space-y-6">
                <div className="bg-amber-50 p-6 rounded-2xl border-2 border-amber-200">
                    <h3 className="font-bold text-xl text-amber-800 mb-3">Local vs Global</h3>
                    <div className="space-y-4">
                        <div className="flex items-start gap-3">
                            <div className="bg-amber-200 p-2 rounded text-amber-800 text-xl">🏠</div>
                            <div>
                                <h4 className="font-bold text-amber-900">Local Scope</h4>
                                <p className="text-slate-700 text-sm">
                                    Variables created INSIDE a function only exist safely inside that function.
                                    Like ingredients <em>inside</em> the oven!
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="bg-blue-200 p-2 rounded text-blue-800 text-xl">🌍</div>
                            <div>
                                <h4 className="font-bold text-blue-900">Global Scope</h4>
                                <p className="text-slate-700 text-sm">
                                    Variables created OUTSIDE exist everywhere. Anyone can touch them!
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="bg-purple-50 p-4 rounded-xl border border-purple-200">
                    <h4 className="font-bold text-purple-800 mb-2">🎮 Ready to Bake?</h4>
                    <p className="text-purple-700 text-sm">
                        In the activity, you'll use a function machine to bake a cake!
                    </p>
                </div>
            </div>
        )
    }
];

// ============ GAME DATA ============
const LEVELS = [
    { id: 1, name: "Basic Sponge", ingredients: ["🌾 Flour", "🥚 Eggs"], desc: "Let's start simple. Bake a basic sponge cake." },
    { id: 2, name: "Morning Pancakes", ingredients: ["🌾 Flour", "🥛 Milk"], desc: "The customer wants pancakes for breakfast!" },
    { id: 3, name: "Chocolate Brownie", ingredients: ["🌾 Flour", "🍫 Choco"], desc: "Something sweet and simple." },
    { id: 4, name: "Custard Cream", ingredients: ["🥚 Eggs", "🥛 Milk"], desc: "Just the wet ingredients for a custard base." },
    { id: 5, name: "Rich Cake", ingredients: ["🌾 Flour", "🥚 Eggs", "🥛 Milk"], desc: "A moist cake requires milk too!" },
    { id: 6, name: "Chocolate Cake", ingredients: ["🌾 Flour", "🥚 Eggs", "🍫 Choco"], desc: "A classic birthday cake." },
    { id: 7, name: "Hot Cocoa Mix", ingredients: ["🥛 Milk", "🍫 Choco"], desc: "Warm it up! Just milk and chocolate." },
    { id: 8, name: "Double Choco Bomb", ingredients: ["🌾 Flour", "🍫 Choco", "🍫 Choco"], desc: "Double the chocolate, double the flavor!" },
    { id: 9, name: "The Works", ingredients: ["🌾 Flour", "🥚 Eggs", "🥛 Milk", "🍫 Choco"], desc: "Use everything we have!" },
    { id: 10, name: "Gigantic Wedding Cake", ingredients: ["🌾 Flour", "🌾 Flour", "🥚 Eggs", "🥚 Eggs", "🥛 Milk"], desc: "A double batch! Two Layes of Flour and Eggs, plus Milk." }
];

// ============ GAME COMPONENT ============
const BakingGame = ({ onComplete }: { onComplete: () => void }) => {
    const [ingredients, setIngredients] = useState<string[]>([]);
    const [isBaking, setIsBaking] = useState(false);
    const [gameState, setGameState] = useState<'IDLE' | 'BAKING' | 'SUCCESS' | 'FAIL'>('IDLE');
    const [currentLevelIdx, setCurrentLevelIdx] = useState(0);
    const [feedback, setFeedback] = useState("");

    const currentLevel = LEVELS[currentLevelIdx];
    const isFinished = currentLevelIdx >= LEVELS.length;

    const addIngredient = (ing: string) => {
        if (isBaking || gameState === 'SUCCESS') return;
        setIngredients(prev => [...prev, ing]);
        setGameState('IDLE');
    };

    const runFunction = () => {
        if (ingredients.length === 0) return;
        setIsBaking(true);
        setGameState('BAKING');

        // Verify Logic
        const sortedInput = [...ingredients].sort().join(',');
        const sortedTarget = [...currentLevel.ingredients].sort().join(',');

        setTimeout(() => {
            setIsBaking(false);

            if (sortedInput === sortedTarget) {
                // Success
                setGameState('SUCCESS');
                setFeedback("Perfect Match! 🌟");

                // Auto Advance after delay
                setTimeout(() => {
                    if (currentLevelIdx < LEVELS.length - 1) {
                        setIngredients([]);
                        setGameState('IDLE');
                        setCurrentLevelIdx(prev => prev + 1);
                        setFeedback("");
                    } else {
                        onComplete(); // Module Complete
                    }
                }, 2000);
            } else {
                // Fail
                setGameState('FAIL');

                // Hints
                if (ingredients.length < currentLevel.ingredients.length) setFeedback("Missing ingredients! Recipe is incomplete.");
                else if (ingredients.length > currentLevel.ingredients.length) setFeedback("Too many ingredients! It's messy.");
                else setFeedback("Wrong ingredients! Check the recipe.");
            }
        }, 2000);
    };

    const reset = () => {
        setIngredients([]);
        setGameState('IDLE');
        setFeedback("");
    };

    if (isFinished) {
        return (
            <div className="bg-slate-900 rounded-3xl p-12 text-center text-white border-4 border-green-500 shadow-2xl">
                <ChefHat size={80} className="mx-auto text-green-400 mb-6" />
                <h2 className="text-4xl font-bold mb-4">Master Chef!</h2>
                <p className="text-xl text-slate-300">You've completed all orders perfectly.</p>
                <div className="mt-8 animate-pulse text-green-400 font-bold">Module Completed</div>
            </div>
        )
    }

    return (
        <div
            className="rounded-3xl p-8 text-white shadow-2xl relative overflow-hidden"
            style={{
                background: `linear-gradient(to bottom, rgba(30, 27, 75, 0.9), rgba(49, 46, 129, 0.9)), url('/assets/function-oven-bg.png')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
            }}
        >
            <div className="text-white space-y-8">
                {/* HUD / Order Ticket */}
                <div className="bg-yellow-50 text-slate-900 p-6 rounded-2xl shadow-xl border-t-8 border-yellow-400 relative overflow-hidden">
                    <div className="absolute top-0 right-0 bg-yellow-400 text-yellow-900 font-bold px-4 py-1 rounded-bl-xl text-sm">
                        ORDER #{currentLevel.id}
                    </div>
                    <h3 className="text-2xl font-black font-serif mb-2 flex items-center gap-3">
                        <span className="text-3xl">📝</span> {currentLevel.name}
                    </h3>
                    <p className="text-slate-700 font-medium text-lg border-l-4 border-yellow-300 pl-4 mb-4">{currentLevel.desc}</p>

                    {/* Recipe Hints */}
                    <div className="bg-yellow-100/50 p-3 rounded-lg border border-yellow-200">
                        <span className="text-xs font-bold uppercase tracking-wider text-yellow-800 mb-2 block">Required Ingredients:</span>
                        <div className="flex flex-wrap gap-2">
                            {currentLevel.ingredients.map((ing, i) => (
                                <span key={i} className="bg-white px-2 py-1 rounded shadow-sm text-sm border border-yellow-100 text-slate-700">
                                    {ing}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mt-6 flex items-center gap-4">
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Service Progress</span>
                        <div className="flex-1 h-3 bg-yellow-200 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-yellow-600 transition-all duration-500"
                                style={{ width: `${((currentLevelIdx) / LEVELS.length) * 100}%` }}
                            />
                        </div>
                        <span className="text-sm font-bold text-slate-600">{currentLevelIdx + 1}/{LEVELS.length}</span>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start w-full">

                    {/* LEFT: Code Representation */}
                    <div className="bg-slate-900/80 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-white/10 relative overflow-hidden group min-h-[400px]">
                        <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity">
                            <Box size={120} />
                        </div>

                        {/* Code Content */}
                        <div className="font-mono text-sm md:text-base leading-loose relative z-10 overflow-x-auto custom-scrollbar">
                            <div className="whitespace-nowrap">
                                <span className="text-purple-400 font-bold">function</span> <span className="text-yellow-400 font-bold">bakeCake</span>(
                                <span className="text-orange-400">ingredients</span>
                                ) {'{'}
                            </div>

                            {/* Indented Scope Block */}
                            <div className="pl-6 py-4 border-l-2 border-slate-700 ml-2 my-2 bg-white/5 rounded-r-lg backdrop-blur-sm min-h-[160px]">
                                {ingredients.length === 0 ? (
                                    <span className="text-slate-500 italic block whitespace-nowrap">// Select ingredients to prepare order...</span>
                                ) : (
                                    <div className="flex flex-col gap-1">
                                        {ingredients.map((ing, i) => (
                                            <motion.div
                                                initial={{ x: -20, opacity: 0 }}
                                                animate={{ x: 0, opacity: 1 }}
                                                key={i}
                                                className="whitespace-nowrap"
                                            >
                                                <span className="text-blue-400">oven</span>.
                                                <span className="text-green-400">add</span>(
                                                <span className="text-orange-300">"{ing}"</span>
                                                );
                                            </motion.div>
                                        ))}
                                    </div>
                                )}

                                {/* Result Logic Display */}
                                <div className="mt-8 pt-4 border-t border-white/10">
                                    {gameState === 'FAIL' ? (
                                        <div className="text-red-400 font-bold flex items-center gap-2">
                                            ⚠️ Error: {feedback}
                                        </div>
                                    ) : gameState === 'SUCCESS' ? (
                                        <div className="text-green-400 font-bold flex items-center gap-2">
                                            ✅ Returns: "Perfect {currentLevel.name}"
                                        </div>
                                    ) : (
                                        <span className="text-purple-400">return</span>
                                    )}
                                </div>
                            </div>
                            <div>{'}'}</div>
                        </div>
                    </div>

                    {/* RIGHT: Visual Machine */}
                    <div className="flex flex-col items-center gap-8">
                        {/* Ingredients Shelf */}
                        <div className="w-full">
                            <h3 className="text-slate-300 text-sm font-bold uppercase tracking-wider mb-3 text-center">Pantry</h3>
                            <div className="grid grid-cols-2 gap-4 w-full">
                                {['🥚 Eggs', '🥛 Milk', '🌾 Flour', '🍫 Choco'].map((item) => (
                                    <motion.button
                                        whileHover={{ scale: 1.05, y: -2 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={() => addIngredient(item)}
                                        disabled={isBaking || gameState === 'SUCCESS'}
                                        key={item}
                                        className="bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 hover:border-purple-400 disabled:opacity-30 disabled:cursor-not-allowed px-4 py-3 rounded-xl shadow-lg text-lg font-bold text-white transition-all flex items-center justify-center gap-2"
                                    >
                                        {item}
                                    </motion.button>
                                ))}
                            </div>
                        </div>

                        {/* Oven Machine */}
                        <div className="relative w-full max-w-md aspect-square bg-slate-800 rounded-[3rem] p-6 border-b-[12px] border-slate-900 shadow-2xl flex flex-col items-center justify-between overflow-hidden">
                            {/* Oven Window */}
                            <div className={`w-full flex-1 rounded-3xl border-4 relative overflow-hidden flex items-center justify-center shadow-inner transition-colors duration-500 ${gameState === 'FAIL' ? 'bg-red-900/20 border-red-500/50' :
                                gameState === 'SUCCESS' ? 'bg-green-900/20 border-green-500/50' :
                                    'bg-slate-900 border-slate-700'
                                }`}>
                                <AnimatePresence mode='wait'>
                                    {isBaking ? (
                                        <motion.div
                                            key="baking"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            className="absolute inset-0 bg-orange-600/30 flex items-center justify-center backdrop-blur-sm"
                                        >
                                            <motion.div
                                                animate={{ rotate: 360 }}
                                                transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                                                className="text-8xl filter drop-shadow-[0_0_20px_rgba(251,146,60,0.8)]"
                                            >
                                                🌀
                                            </motion.div>
                                        </motion.div>
                                    ) : gameState === 'SUCCESS' ? (
                                        <motion.div
                                            key="cake"
                                            initial={{ scale: 0, rotate: -10 }}
                                            animate={{ scale: 1.2, rotate: 0 }}
                                            className="text-9xl filter drop-shadow-2xl relative z-10"
                                        >
                                            🎂
                                        </motion.div>
                                    ) : (
                                        <div className="flex flex-wrap justify-center gap-2 p-4 max-w-[80%]">
                                            {ingredients.map((ing, i) => (
                                                <motion.div
                                                    initial={{ scale: 0, y: 20 }}
                                                    animate={{ scale: 1, y: 0 }}
                                                    key={i}
                                                    className="bg-white text-slate-900 text-xs font-bold px-3 py-1.5 rounded-lg shadow-lg border-b-2 border-slate-300"
                                                >
                                                    {ing}
                                                </motion.div>
                                            ))}
                                            {ingredients.length === 0 && (
                                                <div className="opacity-30 text-center">
                                                    <ChefHat size={48} className="mx-auto mb-2" />
                                                    <span className="text-sm font-bold">Ready to Cook</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </AnimatePresence>
                            </div>

                            {/* Controls Panel */}
                            <div className="w-full mt-6 flex justify-between items-center relative z-10 px-2">
                                <div className="flex gap-2">
                                    <button
                                        onClick={reset}
                                        className="p-3 rounded-xl bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors"
                                        title="Reset Ingredients"
                                    >
                                        <RefreshCw size={20} />
                                    </button>
                                </div>

                                <button
                                    onClick={runFunction}
                                    disabled={ingredients.length === 0 || gameState === 'SUCCESS'}
                                    className={`
                                            font-bold px-8 py-3 rounded-2xl shadow-xl transition-all active:scale-95 flex items-center gap-2 text-lg border-b-4
                                            ${gameState === 'SUCCESS'
                                            ? 'bg-green-500 text-white border-green-700'
                                            : gameState === 'FAIL'
                                                ? 'bg-red-500 hover:bg-red-400 text-white border-red-700'
                                                : ingredients.length > 0
                                                    ? 'bg-blue-500 hover:bg-blue-400 text-white border-blue-700'
                                                    : 'bg-slate-700 text-slate-500 border-slate-800 cursor-not-allowed'
                                        }
                                        `}
                                >
                                    {isBaking ? 'Baking...' : gameState === 'SUCCESS' ? 'Done!' : gameState === 'FAIL' ? 'Try Again' : 'Bake!'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============ MAIN COMPONENT ============
export const FunctionRecipe = () => {
    // Optimized selector
    const completeModule = useAcademyStore(state => state.completeModule);
    const [completed, setCompleted] = useState(false);

    const handleComplete = React.useCallback(() => {
        setCompleted(true);
        completeModule('functions-scope', 50, 'badge_functions');
    }, [completeModule]);

    return (
        <LessonWrapper
            moduleName="Function Oven"
            moduleEmoji="🧁"
            moduleColor="purple"
            lessons={LESSONS}
            completed={completed}
            activityLabel="Activity"
        >
            <BakingGame onComplete={handleComplete} />
        </LessonWrapper>
    );
};

export default FunctionRecipe;
